import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { StatusCoberturaPortal } from '@via-tax/contracts'
import { Aviso, Botao, Selo } from '@via-tax/ui'
import { mesCurto } from '../formatos'
import type { ContextoSessao } from '../tipos'
import { mensagemDeErro, useTRPC } from '../trpc'

/**
 * Notas que chegam do Portal do Cliente sem upload (F2, §5.2): situação da última rodada,
 * meses que o Portal já baixou do SIEG e botão para sincronizar agora.
 */

const COR_MES: Record<StatusCoberturaPortal, string> = {
  ok: 'bg-ok/15 text-ok',
  atualizando: 'bg-navy/10 text-navy',
  aguardando: 'bg-fundo text-tinta-3',
  erro: 'bg-alerta/10 text-alerta',
  futuro: 'bg-fundo text-tinta-3',
}
const ROTULO_MES: Record<StatusCoberturaPortal, string> = {
  ok: 'baixado',
  atualizando: 'buscando no SIEG',
  aguardando: 'na fila do Portal',
  erro: 'erro no SIEG',
  futuro: '—',
}

const quando = (d: string | Date | null) => {
  if (!d) return '—'
  const dt = typeof d === 'string' ? new Date(d) : d
  return dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export function SincronizacaoPortal({ contexto }: { contexto: ContextoSessao }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const pode = (contexto.capacidades as string[]).includes('upload_xml')
  const situacao = useQuery({
    ...trpc.portal.situacao.queryOptions(),
    refetchInterval: (q) => (q.state.data?.status === 'sincronizando' ? 3000 : 60_000),
  })
  const sincronizar = useMutation(
    trpc.portal.sincronizar.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: trpc.portal.pathKey() })
        void qc.invalidateQueries({ queryKey: trpc.documento.pathKey() })
      },
    }),
  )
  const s = situacao.data
  if (!s) return null
  if (!s.configurado && s.status === 'nunca') return null

  // Uma linha de meses por unidade da raiz; o mês conta como baixado quando todas as unidades baixaram.
  const meses = [...new Set(s.cobertura.flatMap((e) => Object.keys(e.competencias)))].sort()
  const piorStatus = (comp: string): StatusCoberturaPortal => {
    const ordem: StatusCoberturaPortal[] = ['erro', 'atualizando', 'aguardando', 'futuro', 'ok']
    const st = s.cobertura.map((e) => e.competencias[comp]?.status ?? 'aguardando')
    return ordem.find((o) => st.includes(o)) ?? 'ok'
  }

  return (
    <section className="rounded-lg border border-linha bg-painel">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-linha px-5 py-3">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold">Notas do Portal do Cliente</h2>
          {s.status === 'sincronizando' && <Selo tom="navy">sincronizando…</Selo>}
          {s.status === 'ok' && <Selo tom="ok">sincronizado</Selo>}
          {s.status === 'erro' && <Selo tom="alerta">falhou</Selo>}
          {s.status === 'nunca' && <Selo>ainda não sincronizado</Selo>}
        </div>
        {pode && s.configurado && (
          <Botao onClick={() => sincronizar.mutate()} disabled={sincronizar.isPending || s.status === 'sincronizando'}>
            {s.status === 'sincronizando' ? 'Sincronizando…' : 'Sincronizar agora'}
          </Botao>
        )}
      </header>
      <div className="flex flex-col gap-3 px-5 py-4 text-sm">
        <p className="text-tinta-2">
          As notas que o Portal já baixou do SIEG entram aqui sem upload, a cada poucas horas, e passam pela mesma conferência do envio manual.
          {s.concluidoEm && (
            <>
              {' '}
              Última rodada {quando(s.concluidoEm)}: <strong className="num text-tinta">{s.enviados}</strong> nota(s) nova(s) ou atualizada(s),{' '}
              <span className="num">{s.existentes}</span> já estavam aqui{s.semXml > 0 ? `, ${s.semXml} sem XML no Portal` : ''}.
            </>
          )}
        </p>
        {s.mensagem && s.status !== 'erro' && <p className="text-xs text-tinta-3">{s.mensagem}</p>}
        {s.status === 'erro' && <Aviso>Não foi possível falar com o Portal: {s.mensagem}. A próxima rodada tenta de novo.</Aviso>}
        {sincronizar.isError && <Aviso>{mensagemDeErro(sincronizar.error)}</Aviso>}
        {sincronizar.isSuccess && s.status !== 'sincronizando' && <Aviso tom="info">Sincronização pedida — as notas aparecem conforme são conferidas.</Aviso>}
        {meses.length > 0 && (
          <div>
            <p className="mb-1.5 text-xs text-tinta-3">Meses do ano-base no Portal</p>
            <ul className="flex flex-wrap gap-1.5">
              {meses.map((m) => {
                const st = piorStatus(m)
                return (
                  <li key={m} className={`num rounded px-2 py-1 text-[11px] ${COR_MES[st]}`} title={ROTULO_MES[st]}>
                    {mesCurto(m)}
                  </li>
                )
              })}
            </ul>
            <p className="mt-1.5 flex flex-wrap gap-3 text-[11px] text-tinta-3">
              <span><span className="inline-block h-2 w-2 rounded-sm bg-ok/60" /> baixado</span>
              <span><span className="inline-block h-2 w-2 rounded-sm bg-navy/40" /> buscando no SIEG</span>
              <span><span className="inline-block h-2 w-2 rounded-sm bg-linha" /> na fila</span>
              <span><span className="inline-block h-2 w-2 rounded-sm bg-alerta/60" /> erro</span>
            </p>
          </div>
        )}
      </div>
    </section>
  )
}
